import  { useEffect, useMemo, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Grid } from "@mui/material";
import { STAUTES } from "../../../../utils/constant";
import {
  base64ToBlob,
  changeMode,
  changeModeForField,
  checkEditEnabled,
  downloadFile,
  fieldTruePermission,
  prepareDefaultValues,
  prepareInitialConfig
} from "../../../../utils/formHelper";
import {
  ADD_RAP_INFO,
  GET_ALL_INDUSTRY_DROPDOWN,
  GET_ALL_JOB_CATEGORY_DROPDOWN,
  GET_ALL_JOB_TITLE_DROPDOWN,
  GET_ALL_SPONSOR_INFORMATION,
  GET_SAA_STATES,
  GET_OA_STATES,
  GET_APPRENTICE_RECORD,
  GET_RAP_INFO,
  UPDATE_RAP_INFO
} from "../../../../utils/services/apiPath";
import { defaultDropdownConfig, transFormModalData } from "../../../../utils/dropdownHelper";
import { getApi, postApi, putApi } from "../../../../utils/services";
import { errorToast, successToast } from "../../../../utils/toastHelper";
import { v4 as uuidv4 } from "uuid";
import { ProSelectField, ProTextInput, ProCheckbox, ProFormsLayout } from "@proscore-ai/proscore-ui-library";

const rapInfoFormConfing = {
  jobTitle: {
    label: "Rap Title",
    perKey: "jobTitle",
    rules: { required: "Rap Title is required!" }
  },
  approvedJobTitle: {
    label: "Approved Labor Classification Title",
    perKey: "approvedJobTitle"
  },
  industry: {
    label: "Industry",
    perKey: "industry",
    rules: { required: "Industry is required!" }
  },
  category: {
    label: "Category",
    perKey: "category",
    rules: { required: "Category is required!" }
  },
  apprenticeshipApproach: {
    label: "Apprenticeship Approach",
    perKey: "apprenticeshipApproach"
  },
  sponsor: {
    label: "Sponsor",
    perKey: "sponsor",
    rules: { required: "Sponsor is required!" }
  },
  summary: {
    label: "Summary",
    perKey: "summary"
  },
  description: {
    label: "Description",
    perKey: "description"
  },
  rapidCode: {
    label: "RAPIDS Code",
    perKey: "rapidCode"
  },
  isDOL: {
    perKey: "isDOL",
    defaultValue: null,
    label: "Is DOL",
    rules: {
      validate: (value, values) => {
        if (values.isSSA) return true;
        return value ? true : "DOL or SAA is required!";
      }
    }
  },
  isSSA: {
    perKey: "isSSA",
    defaultValue: null,
    label: "Is SAA",
    rules: {
      validate: (value, values) => {
        if (values.isDOL) return true;
        return value ? true : "DOL or SAA is required!";
      }
    }
  },
  isMultiEmployer: {
    label: "Is Multi Employer",
    perKey: "isMultiEmployer"
  },
  SSAState: {
    label: "SAA State",
    perKey: "SAAState",
    rules: { required: "State is required!" }
  },
  programLength: {
    label: "Program Length (Years)",
    perKey: "programLength",
    rules: { required: "Program Length is required!" }
  },
  programNumber: {
    label: "Program Number",
    perKey: "programNumber",
    defaultValue: null
  }
};

const ApprenticeshipApproachDropDown = [{ label: "Time-based" }, { label: "Competency-based" }, { label: "Hybrid" }];

export default function RapInfoProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isAddMode = useMemo(() => id === "add", [id]);
  const [status, setStatus] = useState(STAUTES.IDLE);
  const [rapInfoData, setRapInfoData] = useState({});
  const [jobTitleDropDown, setJobTitleDropDown] = useState([]);
  const [industryDropDown, setIndustryDropDown] = useState([]);
  const [jobCategoryDropDown, setJobCategoryDropDown] = useState([]);
  const [ssaStateDropDown, setSsaStateDropDown] = useState([]);
  const [dolStateDropDown, setDolStateDropDown] = useState([]);
  const [apprenticeshipApproachDropDown, setApprenticeshipApproachDropDown] = useState([]);
  const [sponsorDropDown, setSponsorDropDown] = useState([]);
  const [otjHoursArray, setOtjHoursArray] = useState([]);
  const [copyOtjHoursArray, setCopyOtjHoursArray] = useState([]);
  const [formConfig, setFormConfig] = useState(prepareInitialConfig(rapInfoFormConfing, isAddMode ? "edit" : "read"));
  const { handleSubmit, control, setValue, watch, unregister, trigger } = useForm({ defaultValues: prepareDefaultValues(rapInfoFormConfing) });
  const isEditModeOn = useMemo(() => checkEditEnabled(isAddMode, formConfig), [isAddMode, formConfig]);
  const [isSaving, setIsSaving] = useState(false);
  const [originalDOLState, setOriginalDOLState] = useState(null);
  const [originalSAAState, setOriginalSAAState] = useState(null);
  const programLength = watch("programLength");
  const isDOL = watch("isDOL");
  const isSSA = watch("isSSA");

  const backAction = () => navigate("/rap-info");
  const onEdit = () => setFormConfig(changeMode("edit"));
  const onCancel = () => {
    if (isAddMode) backAction();
    else {
      setFormConfig(changeMode("read"));
      setOtjHoursArray(JSON.parse(JSON.stringify(copyOtjHoursArray)));
      setValue("programLength", copyOtjHoursArray.length);
    }
  };

  const getDetailById = useCallback(async id => {
    if (!id) return;
    setStatus(STAUTES.LOADING);
    const data = await getApi(`${GET_RAP_INFO}/${id}`);
    setStatus(STAUTES.IDLE);
    const jobTitle = data?.is180RAP ? data?.clientJobTitle : data?.rapJobTitleModel;
    const rapInfo = {
      ...data,
      SSAState: data?.ssStateModel?.label === "All States" ? null : data?.ssStateModel?.value,
      rapidCode: data?.rapidCode,
      programLength: data?.programLengthYears,
      programNumber: data?.programNumber,
      jobTitle,
      ...transFormModalData({
        industry: data?.industryModel,
        category: data?.categoryModel,
        sponsor: data?.sponsorModel,
        SAAState: data?.ssStateModel,
        ...(typeof jobTitle === "object" ? { jobTitle } : {})
      })
    };

    if (rapInfo.isDOL) {
      setOriginalDOLState(rapInfo.SSAState);
    } else if (rapInfo.isSSA) {
      setOriginalSAAState(rapInfo.SSAState);
    }

    if (data?.programLengthYears) {
      const otjHoursArray = Array.from({ length: data.programLengthYears }, (_, i) => ({
        OTJHoursYear: data?.rapProgram?.otjHoursYear?.[i] || "",
        eLearningHoursYear: data?.rapProgram?.eLearningHoursYear?.[i] || "",
        id: uuidv4()
      }));
      setOtjHoursArray(otjHoursArray);
      setCopyOtjHoursArray(JSON.parse(JSON.stringify(otjHoursArray)));
      otjHoursArray.forEach((_, index) => {
        setValue(`OTJHoursYear${index + 1}`, _.OTJHoursYear);
        setValue(`eLearningHoursYear${index + 1}`, _.eLearningHoursYear);
      });
    }
    setRapInfoData(rapInfo);
    Object.keys(formConfig).forEach(field => setValue(field, rapInfo[field]));
  }, []);

  const handleReadValueClick = name => setFormConfig(changeModeForField(name, "edit"));

  const onSubmit = async data => {
    if (isSaving) return;
    setIsSaving(true);
    setStatus(STAUTES.LOADING);
    const otj = otjHoursArray?.map(ot => parseInt(ot.OTJHoursYear));
    const eLearn = otjHoursArray?.map(ot => parseInt(ot.eLearningHoursYear));
    const payload = {
      rapInfoGuid: isAddMode ? null : id,
      rapJobTitleGuid: data?.jobTitle,
      approvedJobTitle: data?.approvedJobTitle,
      industryTypeGuid: data?.industry,
      categoryGuid: data?.category,
      rapidCode: data?.rapidCode,
      summary: data?.summary,
      description: data?.description,
      programLengthYears: data?.programLength,
      isDOL: !!data?.isDOL,
      isSSA: !!data?.isSSA,
      isMultiEmployer: !!data?.isMultiEmployer,
      ssStateGuid: data?.SSAState,
      sponsorGuid: data?.sponsor,
      apprenticeshipApproach: data?.apprenticeshipApproach,
      rapProgram: {
        otjHoursYear: otj,
        eLearningHoursYear: eLearn,
        rapInfoId: isAddMode ? null : id
      },
      programNumber: data?.programNumber
    };
    const method = isAddMode ? postApi : putApi;
    const { error } = await method(isAddMode ? ADD_RAP_INFO : UPDATE_RAP_INFO, payload);
    setIsSaving(false);
    setStatus(STAUTES.IDLE);
    if (error) return errorToast(error);
    successToast(`RAP Info ${isAddMode ? "Added" : "Updated"} Successfully`);
    backAction();
  };

  const getAllJobTitles = async () => {
    setStatus(STAUTES.LOADING);
    const res = await getApi(GET_ALL_JOB_TITLE_DROPDOWN, defaultDropdownConfig);
    setJobTitleDropDown(res.map(item => ({ label: item.label, value: item.value })));
    setStatus(STAUTES.IDLE);
  };

  const getAllIndustry = async () => {
    setStatus(STAUTES.LOADING);
    const res = await getApi(GET_ALL_INDUSTRY_DROPDOWN, defaultDropdownConfig);
    setIndustryDropDown(res.map(item => ({ label: item.label, value: item.value })));
    setStatus(STAUTES.IDLE);
  };

  const getAllJobCategory = async () => {
    setStatus(STAUTES.LOADING);
    const res = await getApi(GET_ALL_JOB_CATEGORY_DROPDOWN, defaultDropdownConfig);
    setJobCategoryDropDown(res.map(item => ({ label: item.label, value: item.value })));
    setStatus(STAUTES.IDLE);
  };

  const getAllSSAStates = async () => {
    const data = await getApi(GET_SAA_STATES);
    setSsaStateDropDown(data?.filter(item => item.label !== "All States")?.map(item => ({ label: item.label, value: item.value })));
  };

  const getAllDOLStates = async () => {
    const data = await getApi(GET_OA_STATES);
    setDolStateDropDown(data?.filter(item => item.label !== "All States")?.map(item => ({ label: item.label, value: item.value })));
  };

  const getAllApprenticeshipApproachDropDown = async () => {
    setApprenticeshipApproachDropDown(ApprenticeshipApproachDropDown.map(item => ({ label: item.label, value: item.label })));
  };

  const getAllSponsorDropDown = async () => {
    const res = await getApi(GET_ALL_SPONSOR_INFORMATION);
    setSponsorDropDown(res.map(item => ({ label: item.label, value: item.value })));
  };

  const handleInputChange = (key, e, index) => {
    const { value } = e.target;
    const updatedArray = [...otjHoursArray];

    if (index >= 0 && index < updatedArray.length) {
      if (key === "OTJHoursYear" || key === "eLearningHoursYear") {
        updatedArray[index][key] = value;
        setOtjHoursArray(updatedArray);
        setValue(`${key}${index + 1}`, value);
        trigger(`${key}${index + 1}`);
      } else {
        console.error(`Invalid ${key}${index}`);
      }
    } else {
      console.error(`Index ${index} is out of bounds for otjHoursArray`);
    }
  };

  const handleDownload = async () => {
    try {
      const res = await getApi(`${GET_APPRENTICE_RECORD}${id}`);
      console.log(res);
      if (!res?.base64Value || !res?.documentName) {
        throw new Error("Invalid data received from the API");
      }
      const blob = base64ToBlob(res.base64Value);
      const blobUrl = URL.createObjectURL(blob);
      downloadFile(blobUrl, res.documentName);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  useEffect(() => {
    if (programLength) {
      setOtjHoursArray(prev => {
        if (prev.length > programLength) {
          for (let i = +programLength + 1; i <= prev.length; i++) {
            unregister(`OTJHoursYear${i}`);
            unregister(`eLearningHoursYear${i}`);
          }
          return prev.slice(0, programLength);
        }
        return Array.from({ length: programLength }, (_, index) => {
          const { OTJHoursYear = "", eLearningHoursYear = "" } = prev[index] || {};
          return { OTJHoursYear, eLearningHoursYear, id: uuidv4() };
        });
      });
    }
  }, [programLength]);

  useEffect(() => {
    getAllJobTitles();
    getAllIndustry();
    getAllJobCategory();
    getAllSSAStates();
    getAllDOLStates();
    getAllApprenticeshipApproachDropDown();
    getAllSponsorDropDown();
  }, []);

  useEffect(() => {
    if (!isAddMode) getDetailById(id);
  }, [isAddMode, getDetailById, id]);

  useEffect(() => {
    if (typeof isDOL === "boolean") {
      setValue("isSSA", !isDOL);
      trigger("isSSA");

      if (isDOL) {
        setValue("SSAState", originalDOLState || null);
      } else {
        setValue("SSAState", originalSAAState || null);
      }
    }
  }, [isDOL, setValue, originalDOLState, originalSAAState]);

  useEffect(() => {
    if (typeof isSSA === "boolean") {
      setValue("isDOL", !isSSA);
      trigger("isDOL");

      if (isSSA) {
        setValue("SSAState", originalSAAState || null);
      } else {
        setValue("SSAState", originalDOLState || null);
      }
    }
  }, [isSSA, setValue, originalDOLState, originalSAAState]);

  const defaultFormProps = { control, formValues: rapInfoData, isAddMode, handleReadValueClick };
  return (
    <div>
      <ProFormsLayout
        status={status}
        backAction={backAction}
        title={`${isAddMode ? "Add" : "Edit"} Rap Info`}
        backLabel="Go Back to Rap Info"
        beingEdited={isEditModeOn}
        onEdit={onEdit}
        onCancel={onCancel}
        onSave={handleSubmit(onSubmit)}
        isSaving={isSaving}
      >
        <Grid container spacing={2}>
          <ProSelectField
            {...defaultFormProps}
            {...formConfig?.jobTitle}
            {...(rapInfoData.is180RAP === true && { mode: "read" })}
            permission={fieldTruePermission}
            options={jobTitleDropDown}
          />
          <ProTextInput {...defaultFormProps} {...formConfig.approvedJobTitle} permission={fieldTruePermission} />
          <ProTextInput {...defaultFormProps} {...formConfig.programNumber} permission={fieldTruePermission} />
          <ProSelectField {...defaultFormProps} {...formConfig.industry} permission={fieldTruePermission} options={industryDropDown} />
          <ProSelectField {...defaultFormProps} {...formConfig.category} permission={fieldTruePermission} options={jobCategoryDropDown} />
          <ProSelectField
            {...defaultFormProps}
            {...formConfig.apprenticeshipApproach}
            permission={fieldTruePermission}
            options={apprenticeshipApproachDropDown}
          />
          <ProSelectField {...defaultFormProps} {...formConfig.sponsor} permission={fieldTruePermission} options={sponsorDropDown} />
          <ProTextInput {...defaultFormProps} {...formConfig.summary} permission={fieldTruePermission} />
          <ProTextInput {...defaultFormProps} {...formConfig.description} permission={fieldTruePermission} />
          <ProTextInput {...defaultFormProps} {...formConfig.rapidCode} permission={fieldTruePermission} />
          <ProCheckbox {...defaultFormProps} {...formConfig.isDOL} permission={fieldTruePermission} handleReadValueClick={onEdit} />
          <ProCheckbox {...defaultFormProps} {...formConfig.isSSA} permission={fieldTruePermission} handleReadValueClick={onEdit} />
          {(isDOL || isSSA) && (
            <ProSelectField
              {...defaultFormProps}
              {...formConfig.SSAState}
              label={`${isDOL ? "DOL" : "SAA"} State`}
              options={isDOL ? dolStateDropDown : ssaStateDropDown}
              permission={fieldTruePermission}
            />
          )}
          <ProCheckbox {...defaultFormProps} {...formConfig.isMultiEmployer} permission={fieldTruePermission} />
          <ProTextInput {...defaultFormProps} {...formConfig.programLength} permission={fieldTruePermission} type="number" />
          {!!programLength &&
            otjHoursArray.map((_, index) => (
              <div key={_.id} style={{ display: "flex", width: "100%", marginBottom: "10px" }}>
                <div style={{ width: "33.3%", padding: "16px 0 0 16px" }}>
                  <ProTextInput
                    size={12}
                    fieldProps={{ value: _.OTJHoursYear, onChange: e => handleInputChange("OTJHoursYear", e, index) }}
                    control={control}
                    formValues={{ [`OTJHoursYear${index + 1}`]: _.OTJHoursYear }}
                    mode={formConfig.programLength.mode}
                    name={`OTJHoursYear${index + 1}`}
                    label={`OTJ Hours Year ${index + 1}`}
                    rules={{ required: `OTJ Hours Year ${index + 1} is required!` }}
                    permission={fieldTruePermission}
                    type="number"
                  />
                </div>
                <div style={{ width: "33.3%", padding: "16px 0 0 16px" }}>
                  <ProTextInput
                    size={12}
                    fieldProps={{ value: _.eLearningHoursYear, onChange: e => handleInputChange("eLearningHoursYear", e, index) }}
                    control={control}
                    formValues={{ [`eLearningHoursYear${index + 1}`]: _.eLearningHoursYear }}
                    mode={formConfig.programLength.mode}
                    name={`eLearningHoursYear${index + 1}`}
                    label={`Learning Hours Year ${index + 1}`}
                    rules={{ required: `Learning Hours Year ${index + 1} is required!` }}
                    permission={fieldTruePermission}
                    isSaving={isSaving}
                    type="number"
                  />
                </div>
              </div>
            ))}
          {!isAddMode && (
            <Button sx={{ ml: 2 }} variant="contained" onClick={handleDownload}>
              Create Appendix A Document
            </Button>
          )}
        </Grid>
      </ProFormsLayout>
    </div>
  );
}
